var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, styled } from '@mui/material';
import { uniqBy } from 'lodash';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { ErrorMessageBox, FallbackComponent } from '../common';
import { QueueType } from '../multiskill/types';
import { HeadcountChangeTable, HeadcountChangeType } from './headcountChangeTable';
import { useHeadcountChangeLoadAPI, useHeadcountChangeSaveAPI, useSummaryByDisplayMode, useSummaryReportAPI, } from './selectors';
var StyledDialogContent = styled(DialogContent)({
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    minHeight: '50vh',
    maxHeight: '80vh',
    overflowY: 'auto',
});
var validateForm = yup.object({
    headcountChanges: yup
        .array()
        .of(yup.object({
        queueId: yup.number().required(),
        queueName: yup.string().required(),
        lobName: yup.string(),
        headcount: yup.number().nullable().defined().min(0).max(100),
        type: yup
            .mixed()
            .oneOf([HeadcountChangeType.Reduce, HeadcountChangeType.Increase])
            .required(),
        queueLocationIds: yup.array().of(yup.number().required()).optional(),
        locationOptions: yup
            .array()
            .of(yup.object({
            id: yup.number().required(),
            name: yup.string().required(),
        }))
            .optional(),
    }))
        .required(),
});
var mapLocationOption = function (location) {
    var _a;
    return ({
        id: location.queueLocationId,
        name: (_a = location.locationName) !== null && _a !== void 0 ? _a : '',
    });
};
var isValidLocationOption = function (location) { return location.id > 0 && !!location.name; };
var getLocationOptions = function (queues) {
    return uniqBy(queues
        .flatMap(function (queue) { var _a; return (_a = queue.countries) !== null && _a !== void 0 ? _a : []; })
        .flatMap(function (country) { var _a; return (_a = country.locations) !== null && _a !== void 0 ? _a : []; })
        .map(mapLocationOption)
        .filter(isValidLocationOption), 'id');
};
var getQueuesForLob = function (lob) { var _a, _b; return __spreadArray(__spreadArray([], ((_a = lob.defQueues) !== null && _a !== void 0 ? _a : []), true), ((_b = lob.nonDefQueues) !== null && _b !== void 0 ? _b : []), true); };
var getQueueId = function (queue) { var _a; return (_a = queue.queueId) !== null && _a !== void 0 ? _a : 0; };
var getSavedChange = function (queueId, savedChanges) {
    return savedChanges.find(function (change) { return change.queueId === queueId; });
};
var findScenarioQueues = function (summaryData, scenarioId) {
    return (summaryData !== null && summaryData !== void 0 ? summaryData : [])
        .filter(function (report) { return report.id === scenarioId && report.dataType === QueueType.SingleSkill; })
        .flatMap(function (report) { var _a; return (_a = report.queuesByLob) !== null && _a !== void 0 ? _a : []; })
        .flatMap(getQueuesForLob);
};
var buildFormRow = function (lobName, queue, savedChanges, scenarioQueues) {
    var _a, _b;
    var queueId = getQueueId(queue);
    var savedChange = getSavedChange(queueId, savedChanges);
    var locationOptions = getLocationOptions(scenarioQueues.filter(function (item) { return getQueueId(item) === queueId; }));
    return {
        queueId: queueId,
        queueName: queue.name,
        lobName: lobName,
        headcount: (_a = savedChange === null || savedChange === void 0 ? void 0 : savedChange.headcount) !== null && _a !== void 0 ? _a : null,
        type: (_b = savedChange === null || savedChange === void 0 ? void 0 : savedChange.type) !== null && _b !== void 0 ? _b : HeadcountChangeType.Reduce,
        queueLocationIds: savedChange ? savedChange.queueLocationIds : locationOptions.map(function (location) { return location.id; }),
        locationOptions: locationOptions,
    };
};
var buildFormRows = function (scenarioId, summaryData, savedChanges) {
    var _a;
    if (savedChanges === void 0) { savedChanges = []; }
    if (!scenarioId)
        return [];
    var scenarioSummary = summaryData === null || summaryData === void 0 ? void 0 : summaryData.find(function (report) { var _a; return report.id === scenarioId && report.dataType === QueueType.SingleSkill && ((_a = report.year) !== null && _a !== void 0 ? _a : 0) === 0; });
    if (!((_a = scenarioSummary === null || scenarioSummary === void 0 ? void 0 : scenarioSummary.queuesByLob) === null || _a === void 0 ? void 0 : _a.length))
        return [];
    var scenarioQueues = findScenarioQueues(summaryData, scenarioId);
    return scenarioSummary.queuesByLob.flatMap(function (lob) {
        return getQueuesForLob(lob).map(function (queue) { return buildFormRow(lob.lobName, queue, savedChanges, scenarioQueues); });
    });
};
export var HeadcountChangeDialog = function (_a) {
    var open = _a.open, onClose = _a.onClose, scenarioId = _a.scenarioId;
    var t = useTranslation(['report', 'translation']).t;
    var summaryData = useSummaryByDisplayMode();
    var _b = useSummaryReportAPI(), fetchSummaryData = _b[1];
    var _c = useHeadcountChangeLoadAPI(scenarioId), savedHeadcountChanges = _c[0], fetchHeadcountChanges = _c[1], clearSavedHeadcountChanges = _c[2];
    var _d = useHeadcountChangeSaveAPI(scenarioId), headcountChangeData = _d[0], saveHeadcountChange = _d[1], clearHeadcountChangeData = _d[2];
    var defaultValues = useMemo(function () {
        var _a;
        return ({
            headcountChanges: buildFormRows(scenarioId, summaryData, (_a = savedHeadcountChanges.data) !== null && _a !== void 0 ? _a : []),
        });
    }, [scenarioId, summaryData, savedHeadcountChanges.data]);
    var methods = useForm({
        defaultValues: defaultValues,
        resolver: yupResolver(validateForm),
    });
    var handleSubmit = methods.handleSubmit, reset = methods.reset, watch = methods.watch;
    var headcountChanges = watch('headcountChanges');
    var isSaveDisabled = !!headcountChangeData.requesting ||
        headcountChanges.some(function (change) {
            var _a, _b, _c, _d;
            return Number(change.headcount) > 0 &&
                ((_b = (_a = change.locationOptions) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0 &&
                !((_d = (_c = change.queueLocationIds) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0);
        });
    useEffect(function () {
        if (open && scenarioId) {
            fetchHeadcountChanges();
        }
        return clearSavedHeadcountChanges;
    }, [clearSavedHeadcountChanges, fetchHeadcountChanges, open, scenarioId]);
    useEffect(function () {
        if (open) {
            reset(defaultValues);
        }
    }, [defaultValues, open, reset]);
    useEffect(function () {
        if (open && headcountChangeData.data) {
            onClose();
            fetchSummaryData(true);
        }
        return clearHeadcountChangeData;
    }, [clearHeadcountChangeData, fetchSummaryData, headcountChangeData.data, onClose, open]);
    var handleSave = handleSubmit(function (values) {
        var payload = {
            headcountChanges: values.headcountChanges
                .filter(function (change) { return change.queueId > 0 && Number(change.headcount) > 0; })
                .map(function (change) {
                var _a;
                return ({
                    queueId: change.queueId,
                    headcount: Number(change.headcount),
                    type: change.type,
                    queueLocationIds: (_a = change.queueLocationIds) !== null && _a !== void 0 ? _a : [],
                });
            }),
        };
        saveHeadcountChange(payload);
    });
    return (_jsxs(Dialog, __assign({ open: open, onClose: onClose, maxWidth: "lg", fullWidth: true }, { children: [_jsx(DialogTitle, __assign({ variant: "h2" }, { children: t('report:annual.headcountChange.title') })), _jsx(StyledDialogContent, { children: _jsx(FormProvider, __assign({}, methods, { children: _jsxs(FallbackComponent, __assign({ requesting: !!headcountChangeData.requesting || !!savedHeadcountChanges.requesting, overlay: true }, { children: [_jsx(ErrorMessageBox, { errors: [headcountChangeData.error, savedHeadcountChanges.error] }), _jsx(HeadcountChangeTable, {})] })) })) }), _jsxs(DialogActions, __assign({ sx: { justifyContent: 'space-between' } }, { children: [_jsx(Button, __assign({ variant: "outlined", onClick: onClose, disabled: headcountChangeData.requesting }, { children: t('translation:action.cancel') })), _jsx(Button, __assign({ variant: "contained", onClick: handleSave, disabled: isSaveDisabled }, { children: t('translation:action.save') }))] }))] })));
};
